import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLanguage } from './language-context';
import { trapTabKey } from './trap-tab-key';

// Mobile-first bottom-sheet dialog (CLAUDE.md: bottom-sheet dialogs on mobile).
// It slides up from the bottom on small screens and centers as a modal card on
// larger ones (styled in shell.css). Used by the search filters sheet and — with the
// design's grabber (DC:3811-3849) — the tab bar's categories and account sheets.
//
// DC44: 22 px top radius and the design's `sheetUp .24s` entry (VIS §2.1 "Bottom
// sheets"); the page behind stops scrolling while a sheet is open; Escape closes;
// focus moves into the sheet and returns to its opener on close.
export function BottomSheet({
  open,
  onClose,
  title,
  children,
  footer,
  grabber = false,
  hideHead = false,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** The design's 40 × 4.5 px drag handle above the content (decorative). */
  grabber?: boolean;
  /** Content carries its own heading; the title stays as the dialog's accessible name. */
  hideHead?: boolean;
  className?: string;
}) {
  const { t } = useLanguage();
  // Callers often pass an inline closer; reading it through a ref keeps the open
  // effect (focus, scroll lock) from re-running — and stealing focus — on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    sheetRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
      else trapTabKey(e, sheetRef.current);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        ref={sheetRef}
        tabIndex={-1}
        className={className ? `sheet ${className}` : 'sheet'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        {grabber && <span className="sheet__grabber" aria-hidden="true" />}
        {hideHead ? (
          <button className="sheet__close sheet__close--float" aria-label={t('shell.close')} onClick={onClose}>
            ✕
          </button>
        ) : (
          <header className="sheet__head">
            <h2 className="sheet__title">{title}</h2>
            <button className="sheet__close" aria-label={t('shell.close')} onClick={onClose}>
              ✕
            </button>
          </header>
        )}
        <div className="sheet__body">{children}</div>
        {footer && <footer className="sheet__foot">{footer}</footer>}
      </div>
    </div>
  );
}
